import axios from "axios";
import { API_URL } from "../api_service";

const HRMS_ORIGIN = "https://hrms.whitestones.in";

// Cache the HRMS token so it can be attached to every TMS API request
// (X-HRMS-AUTH) and survive the post-SSO reload (via sessionStorage).
function cacheHrmsToken(token) {
  if (token) {
    try { sessionStorage.setItem("hrmsToken", token); } catch { /* ignore */ }
  }
}

// The HRMS JWT currently available (module cache or sessionStorage).
export function getHrmsToken() {
  try { return sessionStorage.getItem("hrmsToken"); } catch { return null; }
}

// Decode the (untrusted) 'role' claim from a JWT for client-side UX decisions.
// The backend independently validates the token; this is only for the gate UI.
export function decodeRole(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return (payload.role || "").toString();
  } catch {
    return "";
  }
}

// True when TMS is running inside an iframe (e.g. embedded in HRMS).
export function inIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// Ask the parent HRMS window for its auth token via postMessage.
// Resolves with the token string, or null if not in an iframe / no token
// arrives within timeoutMs.
export function requestHrmsToken(timeoutMs = 20000) {
  return new Promise((resolve) => {
    if (!inIframe()) {
      resolve(null);
      return;
    }

    let settled = false;

    const onMessage = (event) => {
      // Only trust messages from the HRMS origin.
      if (event.origin !== HRMS_ORIGIN) return;
      if (event.data?.type === "HRMS_AUTH") {
        finish(event.data.token || null);
      }
    };

    const finish = (token) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      cacheHrmsToken(token);
      resolve(token);
    };

    window.addEventListener("message", onMessage);

    // Keep asking the parent for the token until it answers (handshake is
    // order-independent: whoever is ready later still completes it). This
    // eliminates the race where the parent listener/iframe isn't ready yet.
    const postReady = () => {
      try {
        window.parent.postMessage({ type: "TMS_READY" }, HRMS_ORIGIN);
      } catch {
        // ignore
      }
    };
    postReady();
    const poll = setInterval(postReady, 400);

    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

// Exchange the HRMS token for a TMS session. Mirrors the login screen's
// sessionStorage writes exactly. Returns the redirect path on success, else null.
export async function ssoLogin(token) {
  let res;
  try {
    res = await axios.post(`${API_URL}/auth/sso`, { token });
  } catch {
    return null;
  }

  if (res?.data?.success && res.data.data?.user) {
    const user = res.data.data.user;
    sessionStorage.setItem("empid", user.trngid);
    sessionStorage.setItem("userId", user.userid);
    sessionStorage.setItem("userRole", user.role.manager ? "MANAGER" : "TRAINEE");
    sessionStorage.setItem("roleName", user.role.roleName);
    sessionStorage.setItem("userName", user.firstname + " " + user.lastname);
    return res.data.data.redirect || "/";
  }

  return null;
}
